import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useContent } from "@/lib/content";
import { X, Send, MessageCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ChatBubble — pré-atendimento estilo Emmy da Emergent.
 *
 * Fluxo:
 *   1. Aguarda `delay_seconds` (config CMS) → mostra a bolha com "greeting".
 *   2. Usuário clica → abre janela de chat.
 *   3. Roteiro fixo (CMS): pergunta nome, telefone, opcional interesse.
 *   4. Após roteiro: libera IA (POST /api/chatbot/chat) para 1..N trocas.
 *   5. Botão "Falar com consultor no WhatsApp" aparece sempre depois do
 *      roteiro fixo, e reforça após `cta_after_turns` trocas.
 *   6. Ao clicar no botão → wa.me/{number}?text={template com {name},{phone},{interest},{page}}
 *   7. Se o usuário fechar sem interagir OU concluir o lead, some pela sessão
 *      (sessionStorage). Nova aba/browser reinicia.
 */

const STORAGE_KEY_DISMISSED = "places_chatbot_dismissed";
const STORAGE_KEY_LEAD_SAVED = "places_chatbot_lead_saved";

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

function formatTemplate(tpl, vars) {
  return String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => vars[k] || "");
}

export default function ChatBubble({ override, offsetBottom = 0 }) {
  // `offsetBottom` empurra o widget pra cima quando há outro botão flutuante
  // na mesma esquina (ex: o balão de WhatsApp da LP).
  const bottomStyle = useMemo(() => ({ bottom: 24 + Number(offsetBottom || 0) }), [offsetBottom]);
  const { chatbot_config } = useContent();
  // `override` permite ligar/configurar o bot por Landing Page (número de
  // WhatsApp, mensagem, delay) sem mexer na config global. Campos vazios do
  // override são ignorados → herdam o /admin/chatbot.
  const cfg = useMemo(() => {
    const ov = Object.fromEntries(
      Object.entries(override || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );
    return { ...(chatbot_config || {}), ...ov };
  }, [chatbot_config, override]);
  const location = useLocation();

  const enabled = !!cfg?.enabled;
  const hidePaths = useMemo(() => (override ? [] : (Array.isArray(cfg?.hide_on_paths) ? cfg.hide_on_paths : [])), [cfg, override]);
  const hiddenHere = hidePaths.some((p) => p && location.pathname.startsWith(p));

  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // index in cfg.script; when >= script.length → free IA chat
  const [messages, setMessages] = useState([]); // {role:"bot"|"user", text}
  const [captured, setCaptured] = useState({ name: "", phone: "", interest: "" });
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [leadSaved, setLeadSaved] = useState(false);
  const [aiTurns, setAiTurns] = useState(0);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  const script = useMemo(() => Array.isArray(cfg?.script) ? cfg.script : [], [cfg]);
  const inScript = step < script.length;
  const showWhatsappCta = !inScript && (aiTurns >= (Number(cfg?.cta_after_turns) || 3) || leadSaved);

  // Timer to reveal the bubble after `delay_seconds` — resets on route change.
  useEffect(() => {
    if (!enabled || hiddenHere) { setBubbleVisible(false); return; }
    if (typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY_DISMISSED)) return;
    if (open) return;

    const delay = Math.max(1, Number(cfg?.delay_seconds) || 20) * 1000;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setBubbleVisible(true), delay);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [enabled, hiddenHere, open, cfg?.delay_seconds, location.pathname]);

  // Autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, thinking]);

  const openChat = () => {
    setOpen(true);
    setBubbleVisible(false);
    if (messages.length === 0) {
      const intro = String(cfg?.intro_message || "").trim();
      const first = script[0];
      const initial = [];
      if (intro) initial.push({ role: "bot", text: intro });
      if (first) initial.push({ role: "bot", text: formatTemplate(first.question, captured) });
      setMessages(initial);
    }
  };

  const dismiss = () => {
    setOpen(false);
    setBubbleVisible(false);
    try { sessionStorage.setItem(STORAGE_KEY_DISMISSED, "1"); } catch { /* noop */ }
  };

  const persistLead = async (finalCaptured, conversation) => {
    try {
      await axios.post(`${API}/chatbot/lead`, {
        name: finalCaptured.name,
        phone: finalCaptured.phone,
        interest: finalCaptured.interest,
        page_url: typeof window !== "undefined" ? window.location.href : "",
        conversation,
      });
      setLeadSaved(true);
      try { sessionStorage.setItem(STORAGE_KEY_LEAD_SAVED, "1"); } catch { /* noop */ }
    } catch (e) {
      console.warn("chatbot lead save failed", e?.message);
    }
  };

  const sendUserMessage = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    const nextMessages = [...messages, { role: "user", text }];
    setMessages(nextMessages);

    if (inScript) {
      const current = script[step];
      const nextCaptured = { ...captured };
      if (current.field && current.field !== "custom") {
        nextCaptured[current.field] = text;
        setCaptured(nextCaptured);
      }
      const nextStep = step + 1;
      setStep(nextStep);

      // Ask next scripted question OR transition to IA
      if (nextStep < script.length) {
        const q = formatTemplate(script[nextStep].question, nextCaptured);
        setMessages((m) => [...m, { role: "bot", text: q }]);
      } else {
        // Roteiro concluído → salva o lead E envia primeira mensagem da IA
        const closing = `Obrigada${nextCaptured.name ? ", " + nextCaptured.name.split(" ")[0] : ""}! Já anotei aqui. Me conta rapidinho o que você está buscando?`;
        setMessages((m) => [...m, { role: "bot", text: closing }]);
        // Save lead only if we have both name and phone
        if (nextCaptured.name && nextCaptured.phone) {
          await persistLead(nextCaptured, [...nextMessages, { role: "bot", text: closing }]);
        }
      }
    } else {
      // Free IA chat
      setThinking(true);
      try {
        const chatHistory = nextMessages
          .filter((m) => m.text)
          .map((m) => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
        const { data } = await axios.post(`${API}/chatbot/chat`, {
          name: captured.name,
          phone: captured.phone,
          messages: chatHistory,
          system_prompt: cfg?.system_prompt || "",
        });
        const reply = data?.reply || "Perfeito! Vou te conectar com um consultor pra continuar.";
        setMessages((m) => [...m, { role: "bot", text: reply }]);
        setAiTurns((n) => n + 1);
        // Store interest = last user message (short summary)
        setCaptured((c) => ({ ...c, interest: c.interest || text.slice(0, 300) }));
      } catch (e) {
        setMessages((m) => [...m, { role: "bot", text: "Estou com um probleminha por aqui. Que tal falarmos direto pelo WhatsApp?" }]);
      } finally { setThinking(false); }
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendUserMessage(); } };

  const goToWhatsapp = () => {
    const number = String(cfg?.whatsapp_number || "").replace(/\D/g, "");
    if (!number) return;
    const vars = {
      name: captured.name || "",
      phone: captured.phone || "",
      interest: captured.interest || "",
      page: typeof window !== "undefined" ? window.location.href : "",
    };
    const msg = formatTemplate(cfg?.whatsapp_message_template, vars);
    const url = `https://wa.me/${number}${msg ? `?text=${encodeURIComponent(msg)}` : ""}`;
    // Save one last time (in case IA path was used and lead already saved, backend simply gets a second lead entry — acceptable)
    if (captured.name && captured.phone && !leadSaved) {
      persistLead(captured, messages);
    }
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener");
    dismiss();
  };

  if (!enabled || hiddenHere) return null;

  const initials = String(cfg?.attendant_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <>
      {/* Floating bubble greeting (before chat opens) */}
      {bubbleVisible && !open && (
        <div style={bottomStyle} className="fixed right-6 z-[70] flex flex-col items-end gap-2 animate-[fadein_0.4s_ease-out]" data-testid="chatbot-bubble">
          <button onClick={openChat} data-testid="chatbot-bubble-open"
            className="bg-white shadow-2xl border border-black/10 rounded-2xl p-3 max-w-[260px] text-left relative hover:shadow-2xl transition-transform hover:scale-[1.02]">
            <div className="flex items-start gap-2">
              <Avatar cfg={cfg} initials={initials} size={40} />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-accent-orange font-bold">{cfg?.attendant_name || "Marina"}</div>
                <div className="text-[10px] text-neutral-500 mb-1">{cfg?.attendant_role || "Consultora"}</div>
                <div className="text-sm text-black leading-snug">{cfg?.greeting || "Posso te ajudar?"}</div>
              </div>
            </div>
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          </button>
          <button onClick={dismiss} data-testid="chatbot-bubble-dismiss"
            className="text-[10px] text-neutral-500 hover:text-black px-2 py-1 bg-white/80 rounded-full backdrop-blur">agora não</button>
        </div>
      )}

      {/* Persistent avatar (once bubble is dismissed but user might still want to open chat via avatar) */}
      {!open && !bubbleVisible && (
        <button onClick={openChat} data-testid="chatbot-avatar"
          style={bottomStyle} className="fixed right-6 z-[65] w-14 h-14 rounded-full bg-accent-yellow shadow-lg flex items-center justify-center hover:scale-105 transition-transform">
          <MessageCircle className="w-6 h-6 text-black" />
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div style={bottomStyle} className="fixed right-6 z-[80] w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-white shadow-2xl rounded-2xl border border-black/10 flex flex-col overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <header className="bg-black text-white p-4 flex items-center gap-3">
            <Avatar cfg={cfg} initials={initials} size={40} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{cfg?.attendant_name || "Marina"}</div>
              <div className="text-[11px] text-white/60 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> online agora
              </div>
            </div>
            <button onClick={dismiss} data-testid="chatbot-close" className="text-white/60 hover:text-white p-1"><X className="w-5 h-5" /></button>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-neutral-50" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chatbot-msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-snug ${m.role === "user" ? "bg-accent-yellow text-black" : "bg-white border border-black/10 text-black"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start" data-testid="chatbot-thinking">
                <div className="max-w-[80%] rounded-2xl px-3 py-2 text-sm bg-white border border-black/10 text-neutral-500 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> digitando…
                </div>
              </div>
            )}
            {showWhatsappCta && (
              <div className="pt-2" data-testid="chatbot-cta-wrapper">
                <button onClick={goToWhatsapp} data-testid="chatbot-whatsapp-cta"
                  className="w-full bg-green-600 text-white text-sm font-semibold rounded-full px-4 py-3 hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                  <svg viewBox="0 0 32 32" className="w-4 h-4 fill-current"><path d="M16 3C9 3 3.5 8.5 3.5 15.4c0 2.4.7 4.7 1.9 6.7L3 29l7.2-2.3c1.9 1 4 1.6 6.2 1.6h.1c6.9 0 12.4-5.5 12.4-12.4C28.9 8.5 23 3 16 3zm0 22.6h-.1c-1.9 0-3.8-.5-5.4-1.5l-.4-.2-4.3 1.4 1.4-4.2-.3-.4A10 10 0 0 1 5.9 15.4c0-5.5 4.5-10 10.1-10 5.6 0 10 4.5 10 10 0 5.6-4.4 10.2-10 10.2zm5.5-7.5c-.3-.1-1.8-.9-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.7.1-.3-.1-1.3-.4-2.4-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.1-.3.2-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.7-.9-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .2.2 2.1 3.1 5 4.3 2.9 1.2 2.9.8 3.4.8.5-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.3z"/></svg>
                  {cfg?.cta_label || "Falar com um consultor no WhatsApp"}
                </button>
                <div className="text-[10px] text-neutral-400 text-center mt-1">A conversa continua no seu WhatsApp.</div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-black/10 bg-white p-2 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
              data-testid="chatbot-input"
              placeholder={inScript ? (script[step]?.placeholder || "Digite aqui…") : "Digite sua mensagem…"}
              disabled={thinking}
              className="flex-1 border border-black/15 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-black" />
            <button onClick={sendUserMessage} disabled={!input.trim() || thinking}
              data-testid="chatbot-send"
              className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:bg-accent-orange transition-colors disabled:opacity-30"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}

function Avatar({ cfg, initials, size = 40 }) {
  const src = resolveSrc(cfg?.attendant_avatar);
  const s = { width: size, height: size };
  if (src) return <img src={src} alt="" className="rounded-full object-cover border-2 border-white/20" style={s} />;
  return (
    <div className="rounded-full bg-accent-yellow flex items-center justify-center font-display text-black" style={s}>
      <span className="text-lg">{initials || "?"}</span>
    </div>
  );
}
